import axios from "axios";
import { Download, Lock, ShoppingCart } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMe, getCachedUser } from "../utils/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const radioApi = axios.create({ baseURL: API, withCredentials: true });
const PAYHIP_SRC = "https://payhip.com/payhip.js";
const PRODUCT_URL = "https://payhip.com/b/lcCf3";
const COVER_COST = 10000;
let payhipLoad;

function loadPayhip() {
  if (payhipLoad) return payhipLoad;
  payhipLoad = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PAYHIP_SRC}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Payhip blocked")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = PAYHIP_SRC;
    script.async = true;
    script.dataset.payhipBuyButton = "true";
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error("Payhip blocked or unavailable"));
    document.head.appendChild(script);
  });
  return payhipLoad;
}

export function PayhipEmbed() {
  const [account, setAccount] = useState(() => getCachedUser());
  const [drop, setDrop] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");
  const [coverVersion, setCoverVersion] = useState(0);
  const [payhipReady, setPayhipReady] = useState(false);
  const [payhipBlocked, setPayhipBlocked] = useState(false);

  useEffect(() => {
    let active = true;
    loadPayhip()
      .then(() => active && setPayhipReady(true))
      .catch(() => active && setPayhipBlocked(true));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const loadStatus = async (user) => {
      if (!user) {
        setDrop(null);
        return;
      }
      try {
        const { data } = await radioApi.get("/radio/cd-cover/status");
        if (active) setDrop(data);
      } catch {
        if (active) setClaimError("PTS VAULT SIGNAL LOST — PURCHASE OPTION REMAINS ONLINE.");
      }
    };
    fetchMe().then((user) => {
      if (!active) return null;
      setAccount(user);
      return loadStatus(user);
    });
    const onAuth = (event) => {
      const user = event.detail?.user || null;
      setAccount(user);
      loadStatus(user);
    };
    window.addEventListener("dolo-auth", onAuth);
    return () => {
      active = false;
      window.removeEventListener("dolo-auth", onAuth);
    };
  }, []);

  const points = drop?.points || 0;
  const unlocked = !!drop?.unlocked;
  const remaining = Math.max(0, COVER_COST - points);
  const progress = Math.min(100, (points / COVER_COST) * 100);
  const coverSrc = unlocked
    ? `${API}/radio/cd-cover/file${coverVersion ? `?v=${coverVersion}` : ""}`
    : "/assets/radioactive-dust-cover-preview.webp";

  const claimCover = async () => {
    setClaiming(true);
    setClaimError("");
    try {
      const { data } = await radioApi.post("/radio/cd-cover/claim");
      setDrop(data);
      setCoverVersion(Date.now());
    } catch (error) {
      setClaimError(error.response?.data?.detail || "CLAIM FAILED — TRY THE $5 PAYHIP OPTION.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <section id="radioactive-dust-drop" className="payhip-embed-section supply-drop-section" data-testid="payhip-embed-section" aria-label="Radioactive Dust CD cover release">
      <div className="payhip-embed-head">
        <span>OFFICIAL SUPPLY DROP</span>
        <strong>Radioactive Dust — CD Cover Release</strong>
        <p>Unlock free with 10,000+ PTS or purchase the release for $5.</p>
      </div>
      <div className="supply-drop-grid">
        <figure className={`supply-cover ${unlocked ? "is-unlocked" : "is-locked"}`} data-testid="cd-cover-art">
          <img src={coverSrc} alt="Radioactive Dust — Dolo the Wasteland Runner Vol. 1 Soundtrack CD cover" />
          {!unlocked ? <figcaption><Lock size={15} /> LOCKED RELEASE</figcaption> : null}
        </figure>
        <div className="supply-drop-actions">
          <div className="supply-points-panel" data-testid="cd-cover-points-panel">
            <span>PTS UNLOCK</span>
            <strong data-testid="cd-cover-points">{account ? `${points.toLocaleString()} / ${COVER_COST.toLocaleString()} PTS` : "SIGN IN REQUIRED"}</strong>
            <div className="supply-progress" aria-hidden="true"><i style={{ width: `${account ? progress : 0}%` }} /></div>
            <small>{account && !unlocked ? `${remaining.toLocaleString()} PTS REMAINING` : unlocked ? "FREE CLAIM REDEEMED" : "LINK YOUR ACCOUNT TO USE PTS"}</small>
          </div>
          {unlocked ? (
            <a className="supply-download" href={`${API}/radio/cd-cover/file?download=1`} data-testid="cd-cover-download-button">
              <Download size={15} /> DOWNLOAD CD COVER
            </a>
          ) : account ? (
            <button type="button" className="supply-claim" onClick={claimCover} disabled={claiming || points < COVER_COST} data-testid="cd-cover-claim-button">
              {claiming ? "CLAIMING…" : points >= COVER_COST ? "UNLOCK FREE — 10,000+ PTS" : `NEED ${remaining.toLocaleString()} PTS`}
            </button>
          ) : (
            <Link to="/account" className="supply-claim is-link" data-testid="cd-cover-signin-link">SIGN IN TO UNLOCK</Link>
          )}
          {claimError ? <p className="supply-claim-error" data-testid="cd-cover-claim-error">{claimError}</p> : null}
          <div className="supply-payhip" data-testid="payhip-buy-wrap">
            <span>OR PURCHASE DIRECT</span>
            {payhipBlocked ? (
              <a className="supply-payhip-fallback" href={PRODUCT_URL} target="_blank" rel="noreferrer" data-testid="payhip-buy-fallback">
                <ShoppingCart size={15} /> BUY NOW — $5
              </a>
            ) : payhipReady ? (
              <a href={PRODUCT_URL} className="payhip-buy-button" data-theme="blue" data-product="lcCf3" data-testid="payhip-buy-button">Buy Now — $5</a>
            ) : (
              <span className="supply-payhip-loading" data-testid="payhip-buy-loading">LOADING PAYHIP…</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
